import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import DateTimePicker from 'src/components/DateTimePicker';
import AutocompleteTextField from 'src/components/AutocompleteTextField';
import Tooltip from 'src/components/Tooltip';
import { InfoIcon } from 'src/components/SkladonIcon/Icons';
import { styled } from '@mui/material/styles';
import {
    ClaimErrorCategories,
    ClaimErrorDepartments,
    ClaimErrorSeverities
} from 'skladon-types';
import { ClaimType } from 'src/types/claim';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import { ConstantsCodeType } from 'src/types/constantsCodes';

export interface ErrorLoggingBlockPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: ClaimType;
    editErrors: FormErrorsType;
    canEditErrorLog: boolean;
}

const InfoIconStyled = styled('span')({
    marginLeft: '10px',
    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});

const departmentOptions: ConstantsCodeType[] = ClaimErrorDepartments.map((value) => ({
    value,
    label: translate(`claimDetail.errorDepartment.${value}`)
}));

const categoryOptions: ConstantsCodeType[] = ClaimErrorCategories.map((value) => ({
    value,
    label: translate(`claimDetail.errorCategory.${value}`)
}));

const severityOptions: ConstantsCodeType[] = ClaimErrorSeverities.map((value) => ({
    value,
    label: translate(`claimDetail.errorSeverity.${value}`)
}));

const ErrorLoggingBlock: FC<ErrorLoggingBlockPropsType> = (props) => {
    const disabled = !props.canEditErrorLog;

    return (
        <Grid>
            <GridItem cols={6}>
                <DateTimePicker
                    value={props.editData.PACKED_AT}
                    name="PACKED_AT"
                    label={translate('claimDetail.packedAt')}
                    type="dateTime"
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={6}>
                <TextField
                    value={props.editData.PACKER_NAME || ''}
                    name="PACKER_NAME"
                    label={translate('claimDetail.packerName')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={6}>
                <TextField
                    value={props.editData.ERROR_RESPONSIBLE || ''}
                    error={props.editErrors.ERROR_RESPONSIBLE}
                    onChange={(value, error) => props.onChange('ERROR_RESPONSIBLE', value || null, error)}
                    name="ERROR_RESPONSIBLE"
                    label={translate('claimDetail.errorResponsible')}
                    fullWidth
                    disabled={disabled}
                />
            </GridItem>
            <GridItem cols={6}>
                <Select
                    options={departmentOptions}
                    value={props.editData.ERROR_DEPARTMENT}
                    error={props.editErrors.ERROR_DEPARTMENT}
                    onChange={(value, error) => props.onChange('ERROR_DEPARTMENT', value || null, error)}
                    name="ERROR_DEPARTMENT"
                    label={translate('claimDetail.errorDepartment')}
                    fullWidth
                    required={props.canEditErrorLog}
                    disabled={disabled}
                />
            </GridItem>
            <GridItem cols={6}>
                <Select
                    options={categoryOptions}
                    value={props.editData.ERROR_CATEGORY}
                    error={props.editErrors.ERROR_CATEGORY}
                    onChange={(value, error) => props.onChange('ERROR_CATEGORY', value || null, error)}
                    name="ERROR_CATEGORY"
                    label={translate('claimDetail.errorCategory')}
                    fullWidth
                    required={props.canEditErrorLog}
                    disabled={disabled}
                />
            </GridItem>
            <GridItem cols={6}>
                <Select
                    options={severityOptions}
                    value={props.editData.ERROR_SEVERITY ?? null}
                    error={props.editErrors.ERROR_SEVERITY}
                    onChange={(value, error) =>
                        props.onChange('ERROR_SEVERITY', value === null || value === undefined ? null : Number(value), error)
                    }
                    name="ERROR_SEVERITY"
                    label={
                        <>
                            {translate('claimDetail.errorSeverity')}
                            <Tooltip title={translate('claimDetail.errorSeverityTooltip')}>
                                <InfoIconStyled>
                                    <InfoIcon />
                                </InfoIconStyled>
                            </Tooltip>
                        </>
                    }
                    fullWidth
                    required={props.canEditErrorLog}
                    disabled={disabled}
                />
            </GridItem>
            <GridItem cols={12}>
                <AutocompleteTextField
                    value={props.editData.ERROR_DESCRIPTION || ''}
                    error={props.editErrors.ERROR_DESCRIPTION}
                    onChange={(value, error) => props.onChange('ERROR_DESCRIPTION', value || null, error)}
                    name="ERROR_DESCRIPTION"
                    label={translate('claimDetail.errorDescription')}
                    fullWidth
                    multiline
                    required={props.canEditErrorLog}
                    disabled={disabled}
                    autoComplete="CLAIM_ERROR_DESCRIPTION"
                />
            </GridItem>
            <GridItem cols={12}>
                <TextField
                    value={props.editData.VIDEO_LINK || ''}
                    error={props.editErrors.VIDEO_LINK}
                    onChange={(value, error) => props.onChange('VIDEO_LINK', value || null, error)}
                    name="VIDEO_LINK"
                    label={translate('claimDetail.videoLink')}
                    fullWidth
                    required={props.canEditErrorLog}
                    disabled={disabled}
                />
            </GridItem>
        </Grid>
    );
};

export default ErrorLoggingBlock;
